/**
 * Cambio password Contabilità (gate FinanceGate).
 * Solo amministratori. Endpoint: POST /api/admin/situazione-economica/change-password
 */
import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import api from "@/api";
import { useFinanceAuth } from "@/context/FinanceAuthContext";

export default function ContabilitaPasswordPanel() {
  const { setPw, clear } = useFinanceAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const change = useMutation({
    mutationFn: (payload) =>
      api.post("/admin/situazione-economica/change-password", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("Password Contabilità aggiornata");
      // Aggiorna la password in memoria per evitare che l'utente venga rilockato:
      setPw(next);
      setCurrent("");
      setNext("");
      setConfirm("");
    },
    onError: (e) =>
      toast.error(e.response?.data?.detail || "Modifica password fallita"),
  });

  const canSubmit = current && next && next === confirm && next.length >= 6;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (next !== confirm) toast.error("Le due nuove password non coincidono");
      else if (next.length < 6) toast.error("Minimo 6 caratteri per la nuova password");
      else toast.error("Compila tutti i campi");
      return;
    }
    change.mutate({ current, new: next });
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-md p-5"
      data-testid="contabilita-pw-panel"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> Password Contabilità
          </div>
          <p className="text-[11px] text-slate-500 mt-1 max-w-2xl">
            Protegge in blocco Costi Fissi, Costi Variabili, Cash Flow, BEP e
            Situazione Economica. La modifica non richiede il redeploy — la
            nuova password è attiva subito.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            clear();
            toast.success("Sezione Contabilità bloccata");
          }}
          data-testid="btn-lock-contabilita"
          title="Blocca ora la sezione Contabilità (forza inserimento password al prossimo accesso)"
          className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1 border border-slate-200 hover:bg-slate-50 rounded-sm text-slate-600 font-semibold"
        >
          <Lock className="w-3 h-3" /> Blocca ora
        </button>
      </div>

      <form onSubmit={handleSubmit} className="grid md:grid-cols-3 gap-3 text-sm">
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">
            Password attuale
          </label>
          <div className="relative">
            <input
              type={showCur ? "text" : "password"} data-no-uppercase
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="pw-current"
              autoComplete="current-password"
              className="w-full pr-9 px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
            <button
              type="button"
              onClick={() => setShowCur((v) => !v)}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
            >
              {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">
            Nuova password
          </label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"} data-no-uppercase
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="pw-new"
              autoComplete="new-password"
              placeholder="min. 6 caratteri"
              className="w-full pr-9 px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
            >
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">
            Ripeti nuova password
          </label>
          <input
            type={showNew ? "text" : "password"} data-no-uppercase
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="pw-confirm"
            autoComplete="new-password"
            className={`w-full px-3 py-2 border rounded-sm text-sm ${
              confirm && confirm !== next
                ? "border-rose-400 bg-rose-50"
                : "border-slate-300"
            }`}
          />
        </div>
        <div className="md:col-span-3 flex items-center justify-between gap-3 pt-1">
          <div className="text-[11px] text-slate-500">
            Suggerimento: dopo il cambio, comunica la nuova password ai
            colleghi che usano l'area Contabilità.
          </div>
          <button
            type="submit"
            disabled={!canSubmit || change.isPending}
            data-testid="btn-change-contabilita-pw"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-semibold rounded-sm"
          >
            <ShieldCheck className="w-4 h-4" />
            {change.isPending ? "Aggiornamento…" : "Cambia password"}
          </button>
        </div>
      </form>
    </section>
  );
}
